import React from "react";
import { teamLogo, isFinished } from "./common";
import { Avatar, Col, Row, Grid } from "antd";
import dayjs from "dayjs";
import PredictOptions from "./PredictOptions";
import { useSelector } from "react-redux";


export default function MatchHero({ fixture, onKickoff }) {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const isLogged = useSelector((state) => state.Auth.isLogged);

  const status = fixture?.fixture?.status?.short;
  const date = fixture?.fixture?.date;
  const venue = fixture?.fixture?.venue;
  const referee = fixture?.fixture?.referee;
  const lg = fixture?.league;
  const home = fixture?.teams?.home;
  const away = fixture?.teams?.away;
  const goals = fixture?.goals;

  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const kickoff = new Date(date).getTime();
  const remain = Math.max(0, Math.floor((kickoff - now) / 1000));
  const h = String(Math.floor(remain / 3600)).padStart(2, "0");
  const m = String(Math.floor((remain % 3600) / 60)).padStart(2, "0");
  const s = String(remain % 60).padStart(2, "0");

  React.useEffect(() => {
    if (status === "NS" && remain === 0 && onKickoff) onKickoff();
  }, [remain, status, onKickoff]);

  const avatarSize = screens.xl ? 90 : screens.lg ? 80 : screens.md ? 80 : 64;
  const scoreFont = screens.xl ? 64 : screens.lg ? 56 : screens.md ? 48 : 38;
  const vsFont = screens.xl ? 40 : screens.lg ? 36 : screens.md ? 32 : 26;
  const teamFont = screens.md ? 18 : 15;

  const flipMinW = isMobile ? 52 : 66;
  const flipFont = isMobile ? 22 : 28;
  const flipGap = isMobile ? 8 : 10;

  const ui = {
    heroWrap: {
      position: "relative",
      padding: "60px 16px",
      background:
        "radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%), linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%)",
      color: "white",
      borderBottom: "1px solid rgba(255,255,255,.06)",
    },
    heroInner: { maxWidth: 1350, margin: "0 auto" },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: "#93c5fd",
      background: "rgba(147,197,253,.10)",
      border: "1px solid rgba(147,197,253,.30)",
      padding: "6px 12px",
      borderRadius: 999,
    },
    heroTitle: {
      margin: "14px 0 10px",
      color: "#fff",
      fontSize: 36,
      lineHeight: 1.15,
      fontWeight: 900,
      letterSpacing: "-0.02em",
    },
    heroSub: { margin: 0, color: "rgba(255,255,255,.85)", maxWidth: 820 },
    crestWrap: {
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "999px",
      padding: 6,
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 10px 22px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
    },
    teamName: {
      color: "rgba(255,255,255,0.9)",
      fontWeight: 700,
      fontSize: teamFont,
      marginTop: 8,
      maxWidth: 180,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
      marginInline: "auto",
    },
    dateText: { margin: 0, color: "rgba(255,255,255,0.85)", fontSize: isMobile ? 12 : 13 },
    statusRow: { marginTop: 6, display: "flex", justifyContent: "center", gap: 8 },
    liveWrap: { position: "relative", display: "inline-flex", gap: 8 },
    liveDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: "rgb(239,68,68)",
      boxShadow: "0 0 0 0 rgba(239,68,68,0.7)",
      animation: "pulse 1.8s ease-out infinite",
      alignSelf: "center",
    },
    liveText: { color: "rgb(252, 165, 165)", fontWeight: 800, fontSize: 12 },

    scoreWrap: {
      display: "inline-flex",
      alignItems: "baseline",
      gap: 10,
      padding: "8px 16px",
      borderRadius: 14,
    },
    scoreNum: {
      fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto",
      fontWeight: 900,
      color: "white",
      lineHeight: 1,
      fontSize: scoreFont,
      minWidth: scoreFont * 0.9,
      textAlign: "center",
    },
    scoreDash: {
      fontWeight: 800,
      color: "rgba(255,255,255,0.9)",
      fontSize: scoreFont * 0.5,
      transform: "translateY(-4px)",
    },
    vsCell: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 800,
      fontSize: vsFont,
      letterSpacing: 1,
      color: "rgba(255,255,255,0.92)",
      lineHeight: 1,
      textTransform: "uppercase",
    },
    flip: {
      display: "flex",
      gap: flipGap,
      justifyContent: "center",
      flexWrap: "nowrap",
    },
    flipGroup: { display: "grid", gap: 6, alignItems: "center", justifyItems: "center" },
    flipCard: {
      minWidth: flipMinW,
      padding: "10px 8px",
      borderRadius: 12,
      background: "rgba(255,255,255,0.06)",
      color: "white",
      fontWeight: 900,
      fontSize: flipFont,
      letterSpacing: 1,
      boxShadow: "0 10px 22px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.06)",
      textAlign: "center",
    },
    flipLabel: { color: "rgba(255,255,255,0.8)", fontSize: 11, fontWeight: 800 },
  };

  return (
    <>
      <section style={ui.heroWrap} aria-label="Hero">
        <div style={ui.heroInner}>
          <Row gutter={24} align="middle">
            <Col xl={12} lg={12} md={0} sm={0} xs={0}>
              <span style={ui.badge}>{lg?.name}, {lg?.country}</span>
              <h1 className="heroTitle" style={ui.heroTitle}>
                {home?.name} VS {away?.name}
              </h1>
              <p style={ui.heroSub}>
                {dayjs(date).format("dddd, DD MMM YYYY • HH:mm")}, {venue?.name}
                {venue?.city ? `, ${venue.city}` : ""}
              </p>
              {referee && <p style={ui.heroSub}>Referee: {referee}</p>}
            </Col>

            <Col xl={0} lg={0} md={24} sm={24} xs={24} style={{ textAlign: 'center', marginBottom: 12 }}>
              <span style={ui.badge}>{lg?.name}, {lg?.country}</span>
            </Col>

            <Col xl={12} lg={12} md={24} sm={24} xs={24}>
              <Row gutter={24} align="middle">
                <Col
                  xl={5}
                  lg={5}
                  md={5}
                  sm={{ span: 10, order: 1 }}
                  xs={{ span: 10, order: 1 }}
                  style={{ textAlign: isMobile ? "center" : "left" }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={ui.crestWrap} aria-label={`${home?.name} crest`}>
                      <Avatar style={{ padding: '10px' }} src={teamLogo(home?.id)} size={avatarSize} />
                    </div>
                  </div>
                </Col>

                <Col
                  xl={0}
                  lg={0}
                  md={0}
                  sm={{ span: 4, order: 2 }}
                  xs={{ span: 4, order: 2 }}
                >
                  <div style={ui.vsCell}>VS</div>
                </Col>

                <Col
                  xl={14}
                  lg={14}
                  md={12}
                  sm={{ span: 24, order: 4 }}
                  xs={{ span: 24, order: 4 }}
                  style={{ textAlign: "center", marginTop: isMobile ? 12 : 0 }}
                >
                  <p style={ui.dateText}>{dayjs(date).format("DD MMM YYYY • HH:mm")}</p>
                  <div style={ui.scoreWrap} aria-live="polite">
                    {isFinished(status) || status === "1H" || status === "2H" || status === "HT" ? (
                      <>
                        <span style={ui.scoreNum}>{goals?.home ?? 0}</span>
                        <span style={ui.scoreDash}> — </span>
                        <span style={ui.scoreNum}>{goals?.away ?? 0}</span>
                      </>
                    ) : (
                      status === "NS" && remain > 0 && (
                        <div style={ui.flip} aria-label="Kickoff countdown">
                          {[h, m, s].map((chunk, i) => (
                            <div key={i} style={ui.flipGroup}>
                              <div style={ui.flipCard}>{chunk}</div>
                              <div style={ui.flipLabel}>{["H", "M", "S"][i]}</div>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                  <div style={ui.statusRow}>
                    {(status === "1H" || status === "2H" || status === "HT") && (
                      <span style={ui.liveWrap} aria-label="Match is live">
                        <span style={ui.liveDot} />
                        <span style={ui.liveText}>LIVE</span>
                      </span>
                    )}
                  </div>
                </Col>

                <Col
                  xl={5}
                  lg={5}
                  md={5}
                  sm={{ span: 10, order: 3 }}
                  xs={{ span: 10, order: 3 }}
                  style={{ textAlign: isMobile ? "center" : "right" }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={ui.crestWrap} aria-label={`${away?.name} crest`}>
                      <Avatar style={{ padding: '10px' }} src={teamLogo(away?.id)} size={avatarSize} />
                    </div>
                  </div>
                </Col>
              </Row>

              <Row gutter={24} className="text-center" style={{ marginTop: 14 }}>
                <Col xl={0} lg={0} md={11} sm={11} xs={11} style={{ textAlign: 'right' }}>
                  {home?.name}
                </Col>
                <Col xl={0} lg={0} md={2} sm={2} xs={2} style={{ fontWeight: 'bold' }}>
                  VS
                </Col>
                <Col xl={0} lg={0} md={11} sm={11} xs={11} style={{ textAlign: 'left' }}>
                  {away?.name}
                </Col>
              </Row>
            </Col>
          </Row>

          {isLogged === true && status === 'NS' && (
            <Row gutter={24}>
              <Col span={24} className="text-center">
                <PredictOptions fixture={fixture} fixtureId={fixture?.fixture?.id} />
              </Col>
            </Row>
          )}
        </div>
      </section>

      <style>{`
        @media (max-width: 640px) {
          .heroTitle { font-size: 28px !important; }
        }
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
          70% { box-shadow: 0 0 0 14px rgba(239,68,68,0); }
          100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
        }
      `}</style>

    </>
  );
}
